import classNames from "classnames";
import PropTypes from "prop-types";
import React from "react";
import formatDate from "shared/@utils/formatDate";
import PermissionBubble from "DreamCast/Shared/PermissionBubble";
import PlaylistCoverArt from "DreamCast/Shared/PlaylistCoverArt";
import { TagBubble } from "shared/TagBubble";
import { ListRowActions } from "shared/ListRowActions";
import styles from "./PlaylistListItem.module.css";

function PlaylistListItem({
  id,
  thumbnails,
  overflowCount,
  title,
  publishDate,
  playlistType,
  location,
  type,
  status,
  isDraft,
  pinPermissions,
  membershipPermissions,
  tags,
  hiddenTags,
  onDuplicate,
  onArchive,
  onEdit,
  isEditLoading,
  isDuplicateLoading,
  isArchiveLoading,
}) {
  const hasSingleCover = thumbnails.length === 1 && overflowCount <= 0;
  const isArchived = String(status || "").toLowerCase() === "archived";
  const activePinPermissions = [
    { key: "ibo", label: "IBO", tone: "neutral" },
    { key: "platinum", label: "PLA", tone: "light" },
    { key: "emerald", label: "EM", tone: "emerald" },
    { key: "diamond", label: "DIA", tone: "teal" },
  ].filter((item) => Boolean(pinPermissions?.[item.key]));
  const activeMembershipPermissions = [
    { key: "complimentary", label: "C", tone: "gray" },
    { key: "premier", label: "P", tone: "teal" },
  ].filter((item) => Boolean(membershipPermissions?.[item.key]));
  const showPinPermissions = activePinPermissions.length > 0;
  const showMembershipPermissions = activeMembershipPermissions.length > 0;
  const showPermissionDivider = showPinPermissions && showMembershipPermissions;

  return (
    <article className={styles.container} aria-label={`Playlist item ${title}`}>
      <div className={styles.topRow}>
        <div className={styles.coverWrap}>
          {hasSingleCover ? (
            <img
              src={thumbnails[0]}
              alt={`${title} cover art`}
              className={styles.singleCoverImage}
            />
          ) : (
            <PlaylistCoverArt
              thumbnails={thumbnails}
              overflowCount={overflowCount}
              className={styles.coverArt}
            />
          )}
        </div>

        <div className={styles.content}>
          <div className={styles.infoCluster}>
            <div className={styles.metaColumn}>
              <p className={styles.title}>{title}</p>
              <p className={styles.date}>{publishDate ? formatDate(publishDate, "M/DD/YYYY") : ""}</p>
              <p className={styles.playlistType}>{playlistType}</p>
            </div>

            <div className={styles.rightColumn}>
              <div className={styles.summaryRow}>
                <p className={styles.valueCell}>{location}</p>
                <p className={classNames(styles.valueCell, styles.typeCell)}>
                  {type}
                </p>
                <p className={classNames(styles.valueCell, styles.statusCell)}>
                  <span
                    className={classNames(styles.statusText, {
                      [styles.archivedStatusText]: isArchived,
                    })}
                  >
                    {status}
                    {isDraft ? (
                      <span className={styles.draftText}> | DRAFT</span>
                    ) : null}
                  </span>
                </p>
              </div>

              <div className={styles.permissionsRow}>
                {showPinPermissions ? (
                  <div
                    className={styles.permissionGroup}
                    aria-label="Pin permissions"
                  >
                    {activePinPermissions.map((permission) => (
                      <PermissionBubble
                        key={permission.key}
                        label={permission.label}
                        tone={permission.tone}
                        active
                      />
                    ))}
                  </div>
                ) : null}

                {showPermissionDivider ? (
                  <span
                    className={styles.permissionDivider}
                    aria-hidden="true"
                  />
                ) : null}

                {showMembershipPermissions ? (
                  <div
                    className={styles.permissionGroup}
                    aria-label="Membership permissions"
                  >
                    {activeMembershipPermissions.map((permission) => (
                      <PermissionBubble
                        key={permission.key}
                        label={permission.label}
                        tone={permission.tone}
                        active
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <ListRowActions
            className={styles.actions}
            onDuplicate={() => onDuplicate(id)}
            onArchive={() => onArchive(id)}
            onEdit={() => onEdit(id)}
            isArchived={isArchived}
            isEditLoading={isEditLoading}
            isDuplicateLoading={isDuplicateLoading}
            isArchiveLoading={isArchiveLoading}
          />
        </div>
      </div>

      {!!(tags.length || hiddenTags.length) && (
        <div className={styles.tagRow}>
          <div className={styles.tagTrack}>
            {/* Regular tags */}
            {tags.slice(0, 8).map((tag, index) => (
              <TagBubble key={`${id}-${tag}-${index}`} label={tag} />
            ))}
            {/* Hidden tags */}
            {hiddenTags.slice(0, Math.max(0, 8 - tags.length)).map((tag, index) => (
              <TagBubble key={`${id}-hidden-${tag}-${index}`} label={tag} tone="gray" />
            ))}
          </div>

          {tags.length + hiddenTags.length > 8 && (
            <span className={styles.overflowDots} aria-hidden="true">
              <span className={styles.overflowDot} />
              <span className={styles.overflowDot} />
              <span className={styles.overflowDot} />
            </span>
          )}
        </div>
      )}
    </article>
  );
}

PlaylistListItem.propTypes = {
  id: PropTypes.string.isRequired,
  thumbnails: PropTypes.arrayOf(PropTypes.string),
  overflowCount: PropTypes.number,
  title: PropTypes.string.isRequired,
  publishDate: PropTypes.string.isRequired,
  playlistType: PropTypes.string,
  location: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  status: PropTypes.oneOf(["Available", "In Review", "Archived", "Upcoming"])
    .isRequired,
  isDraft: PropTypes.bool,
  pinPermissions: PropTypes.shape({
    ibo: PropTypes.bool,
    platinum: PropTypes.bool,
    emerald: PropTypes.bool,
    diamond: PropTypes.bool,
  }),
  membershipPermissions: PropTypes.shape({
    complimentary: PropTypes.bool,
    premier: PropTypes.bool,
  }),
  tags: PropTypes.arrayOf(PropTypes.string),
  hiddenTags: PropTypes.arrayOf(PropTypes.string),
  onDuplicate: PropTypes.func,
  onArchive: PropTypes.func,
  onEdit: PropTypes.func,
  isEditLoading: PropTypes.bool,
  isDuplicateLoading: PropTypes.bool,
  isArchiveLoading: PropTypes.bool,
};

PlaylistListItem.defaultProps = {
  thumbnails: [],
  overflowCount: 0,
  playlistType: "WWG Playlist",
  isDraft: false,
  pinPermissions: {
    ibo: true,
    platinum: true,
    emerald: true,
    diamond: true,
  },
  membershipPermissions: {
    complimentary: true,
    premier: true,
  },
  tags: [],
  hiddenTags: [],
  onDuplicate: () => {},
  onArchive: () => {},
  onEdit: () => {},
  isEditLoading: false,
  isDuplicateLoading: false,
  isArchiveLoading: false,
};

export default PlaylistListItem;
